<template>
  <PreloadedBatteryOfferNextSteps v-if="isPreloadedBatteryOfferNextStepsDisplayed" />
  <div v-else class="flex flex-col gap-8" data-cy="contract-signed-next-steps">
    <div class="flex flex-col gap-1">
      <AtomText
        :text="t('subscription.contractSigned.nextSteps.title')"
        font-size="text-lg"
        font-style="font-semibold"
      />
      <AtomText :text="t('subscription.contractSigned.nextSteps.disclaimer')" font-size="text-xs" />
    </div>

    <NextStepItem
      icon-name="MailIcon"
      icon-color-variant="success"
      :title="t('subscription.contractSigned.nextSteps.email.title')"
      :tags="[{ value: t('subscription.contractSigned.nextSteps.email.tag'), type: 'success' }]"
      data-cy="contract-signed-next-step-email"
    >
      <AtomText
        :text="t('subscription.contractSigned.nextSteps.email.description')"
        font-size="text-sm"
      />
    </NextStepItem>

    <NextStepItem
      v-if="isPilotageSelected"
      icon-name="CurrencyEuroIcon"
      icon-color-variant="economy"
      :title="t('subscription.contractSigned.nextSteps.cashback.title')"
      :tags="[{ value: t('subscription.contractSigned.nextSteps.cashback.tag'), type: 'economy' }]"
      data-cy="contract-signed-next-step-cashback"
    >
      <AtomText
        :text="t('subscription.contractSigned.nextSteps.cashback.description')"
        font-size="text-sm"
      />
    </NextStepItem>
    <NextStepItem
      v-if="!isPilotageSelected"
      icon-name="CurrencyEuroIcon"
      icon-color-variant="economy"
      :title="t('subscription.contractSigned.nextSteps.energySupply.title')"
      :tags="[
        { value: t('subscription.contractSigned.nextSteps.energySupply.tag'), type: 'economy' },
      ]"
      data-cy="contract-signed-next-step-energy-supply"
    >
      <AtomText
        :text="t('subscription.contractSigned.nextSteps.energySupply.description')"
        font-size="text-sm"
      />
    </NextStepItem>

    <NextStepItem
      v-if="isBatterySelected"
      icon-name="MyBatteryIcon"
      icon-color-variant="shy"
      :title="t('subscription.contractSigned.nextSteps.battery.title')"
      :tags="batteryTags"
      data-cy="contract-signed-next-step-battery"
    >
      <p v-if="isSelfInstalled">
        <AtomText is-tag="span" font-size="text-sm" :text="batterySelfInstalledParts[0]" /><AtomText
          is-tag="span"
          font-size="text-sm"
          font-style="font-semibold"
          :text="batterySelfInstalledParts[1]"
        /><AtomText is-tag="span" font-size="text-sm" :text="batterySelfInstalledParts[2]" />
      </p>
      <AtomText
        v-else
        :text="t('subscription.contractSigned.nextSteps.battery.descriptionInstaller')"
        font-size="text-sm"
      />
    </NextStepItem>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSubscriptionStore } from '@/stores';

import { getIsPreloadedBatteryOfferEnabled } from '@/utils/env.util';

import NextStepItem from '@/components/subscription/ContractSigned/NextStepItem.component.vue';
import type { NextStepTag } from '@/components/subscription/ContractSigned/NextStepItem.component.vue';
import PreloadedBatteryOfferNextSteps from '@/components/subscription/ContractSigned/PreloadedBatteryOfferNextSteps.component.vue';

const { t, tm } = useI18n();

const subscriptionStore = useSubscriptionStore();
const {
  isPilotageSelected,
  isBatterySelected,
  isSelfInstalled,
  isEligibleForPreloadedBatteryOffer,
} = storeToRefs(subscriptionStore);

const isPreloadedBatteryOfferNextStepsDisplayed = computed(
  () =>
    getIsPreloadedBatteryOfferEnabled() &&
    isEligibleForPreloadedBatteryOffer.value &&
    isBatterySelected.value
);

const batterySelfInstalledParts = computed(
  () => tm('subscription.contractSigned.nextSteps.battery.descriptionSelfInstalled') as string[]
);

const batteryTags = computed<NextStepTag[]>(() => {
  const tags: NextStepTag[] = [
    { value: t('subscription.contractSigned.nextSteps.battery.tag'), type: 'default' },
  ];

  if (isSelfInstalled.value) {
    tags.push({
      value: t('subscription.contractSigned.nextSteps.battery.actionRequiredTag'),
      type: 'warning',
      variant: 'icon',
      iconName: 'ExclamationIcon',
    });
  }

  return tags;
});
</script>

<style scoped></style>
